import { env } from '$env/dynamic/public'
import {
    createPendoWebSdkClientInitializer,
    createPendoWebSdkOptionsFromEnvironment
} from '@tadpoles/pendo'

import { createAppContext, type AppContext } from '../context/app-context.svelte.js'
import { createAppServices, type AppServices } from '../services/app-services.js'
import { WebConnectivityService } from '../services/connectivity/web.svelte.js'
import { WebLifecycleBridge } from './web-lifecycle-bridge.js'

export type AppRuntime = {
    appContext: AppContext
    appServices: AppServices
    connectivity: WebConnectivityService
    webLifecycle: WebLifecycleBridge
}

let appRuntime: AppRuntime | undefined
let bootstrapPromise: Promise<AppRuntime> | undefined

export function getAppRuntime(): AppRuntime {
    if (appRuntime) {
        return appRuntime
    }

    if (typeof window === 'undefined') {
        throw new Error('App runtime is only available in the browser.')
    }

    const appContext = createAppContext()
    const pendoWebSdkOptions = createPendoWebSdkOptionsFromEnvironment(env)
    const connectivity = new WebConnectivityService({ window })
    const appServices = createAppServices({
        appContext,
        apiBaseUrl: window.location.origin,
        connectivity,
        pendo: pendoWebSdkOptions
            ? {
                  clientInitializer: createPendoWebSdkClientInitializer({
                      ...pendoWebSdkOptions,
                      globalKey: 'tadpolesFamilyPendo'
                  })
              }
            : undefined
    })
    const webLifecycle = new WebLifecycleBridge({
        document,
        lifecycle: appServices.lifecycle,
        window
    })
    appRuntime = {
        appContext,
        appServices,
        connectivity,
        webLifecycle
    }

    return appRuntime
}

export async function bootstrapAppRuntime(): Promise<AppRuntime> {
    bootstrapPromise ??= (async () => {
        const runtime = getAppRuntime()
        await runtime.appServices.lifecycle.initialize()
        runtime.webLifecycle.start()
        await runtime.appServices.auth.restore()

        if (runtime.appServices.auth.state.phase === 'authenticated') {
            await runtime.appServices.userScopedServicesManager.ensureAvailable(
                runtime.appServices.auth.state.sessionContext
            )
        }

        runtime.appServices.pushNotifications.start()
        return runtime
    })()

    return await bootstrapPromise
}
